import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from 'services/api';
import { describeApiError } from 'services/apiErrorMessage';
import axios from 'axios';

import { useStaticData } from 'contexts/StaticDataContext';

const INITIAL_DATA = {
    results: [],
    previous: null,
    next: null,
    count: 0,
    now: 1,
    nowEndpoint: null,
};

export function useProcessList() {
    const { ordersStatus, users } = useStaticData();

    const [data, setData] = useState(INITIAL_DATA);
    const [loader, setLoader] = useState(true);
    const [toast, setToast] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [progress, setProgress] = useState(undefined);
    const [exportMessage, setExportMessage] = useState(null);
    const [exportCancellable, setExportCancellable] = useState(false);

    const timerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const exportButtonRef = useRef(null);

    const showToast = useCallback((type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const formatElapsed = (startTime) => {
        const secs = Math.floor((Date.now() - startTime) / 1000);
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}m ${s}s`;
    };

    const resetExportState = useCallback((button) => {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setLoader(false);
        setExportMessage(null);
        setProgress(undefined);
        setExportCancellable(false);
        if (button) button.disabled = false;
    }, []);

    const cancelExport = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        resetExportState(exportButtonRef.current);
    }, [resetExportState]);

    const getProcessData = useCallback(async (endpoint) => {
        try {
            const response = await api.get(endpoint);
            const { count, next: rawNext, previous: rawPrevious, results } = response.data;
            const next = rawNext?.replace(/^http:\/\//, 'https://') ?? null;
            const previous = rawPrevious?.replace(/^http:\/\//, 'https://') ?? null;
            const pageParam = new URLSearchParams(endpoint.split('?')[1]).get('page');
            const now = pageParam ? parseInt(pageParam) : 1;
            setData({ count, next, previous, results, now, nowEndpoint: endpoint });
            setLoadError(null);
        } catch (error) {
            // Antes só ia para o console e a lista parecia carregar para
            // sempre (incidente de 28/09/2026, precatorios_web#9).
            console.error(error);
            const message = describeApiError(error);
            setLoadError(message);
            showToast('error', message);
        }
    }, [showToast]);

    const reload = useCallback(async (endpoint = '/process/search/', options = { loader: true }) => {
        setLoader(options.loader !== false);
        await getProcessData(endpoint);
        setLoader(false);
    }, [getProcessData]);

    const exportExcel = useCallback((e, filtersUri, filename) => {
        exportButtonRef.current = e.target;
        e.target.disabled = true;

        const startTime = Date.now();
        setExportMessage('Gerando arquivo... 0m 0s');
        setProgress(0);
        setLoader(true);
        setExportCancellable(true);

        timerRef.current = setInterval(() => {
            setExportMessage(`Gerando arquivo... ${formatElapsed(startTime)}`);
        }, 1000);

        abortControllerRef.current = new AbortController();

        api.get(`process/export?${filtersUri}`, {
            responseType: 'blob',
            timeout: 600000,
            signal: abortControllerRef.current.signal,
            onDownloadProgress: (progressEvent) => {
                const elapsed = formatElapsed(startTime);
                if (progressEvent.total) {
                    const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProgress(pct);
                    setExportMessage(`Baixando... ${pct}% — ${elapsed}`);
                } else {
                    const mbs = (progressEvent.loaded / (1024 * 1024)).toFixed(2);
                    setProgress(100);
                    setExportMessage(`Baixando... ${mbs} MB — ${elapsed}`);
                }
            },
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            resetExportState(e.target);
            showToast('success', 'Excel gerado com sucesso.');
        }).catch((error) => {
            if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                return;
            }
            console.error(error);
            resetExportState(e.target);
            if (error.response?.status === 429) {
                showToast('warning', 'Já existe uma exportação em andamento. Aguarde alguns minutos e tente novamente.');
            } else {
                showToast('error', 'Ocorreu um erro ao exportar o arquivo.');
            }
        });
    }, [resetExportState, showToast]);

    useEffect(() => {
        reload('/process/search/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        data,
        ordersStatus,
        users,
        loader,
        toast,
        loadError,
        progress,
        exportMessage,
        exportCancellable,
        showToast,
        reload,
        exportExcel,
        cancelExport,
    };
}
